import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    CircularProgress,
    Alert,
    Chip,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Menu,
    ListItemIcon,
    ListItemText,
    Divider,
    TablePagination,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    Notifications as NotificationsIcon,
    MoreVert as MoreVertIcon,
    Edit as EditIcon,
    School,
    Build,
    Groups,
    Event,
    LocationOn as LocationOnIcon,
    CalendarMonth as CalendarMonthIcon,
} from '@mui/icons-material';
import { PageHeader, AkceFormDialog } from '../../components/admin';
import dayjs from 'dayjs';
import 'dayjs/locale/cs';
import { useAuth } from '../../context/AuthContext';
import type { Akce } from '../../api/JsdhNotifikaceService/generated/models/Akce';
import {
    useSearchAkceQuery,
    useDeleteAkceMutation,
    useSendAkceNotifikaceMutation,
} from '../../api/JsdhNotifikaceService/service/AkceMutations';

dayjs.locale('cs');

const TYP_LABELS: Record<string, string> = {
    SKOLENI: 'Školení',
    KONTROLA_TECHNIKY: 'Kontrola techniky',
    SCHUZKA: 'Schůzka',
    JINE: 'Jiné',
};

const TYP_COLORS: Record<string, 'primary' | 'secondary' | 'success' | 'warning' | 'info' | 'default'> = {
    SKOLENI: 'primary',
    KONTROLA_TECHNIKY: 'warning',
    SCHUZKA: 'info',
    JINE: 'default',
};

const TYP_ICON_COLORS: Record<string, string> = {
    SKOLENI: '#1976d2',
    KONTROLA_TECHNIKY: '#ed6c02',
    SCHUZKA: '#9c27b0',
    JINE: '#757575',
};

const TypIcon: React.FC<{ typ: string }> = ({ typ }) => {
    const props = { sx: { color: TYP_ICON_COLORS[typ] || '#757575', fontSize: 28 } };
    switch (typ) {
        case 'SKOLENI': return <School {...props} />;
        case 'KONTROLA_TECHNIKY': return <Build {...props} />;
        case 'SCHUZKA': return <Groups {...props} />;
        default: return <Event {...props} />;
    }
};

const AkcePage: React.FC = () => {
    const navigate = useNavigate();
    const { isAdmin } = useAuth();

    // Pagination + search
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterTyp, setFilterTyp] = useState('');

    const searchParams = useMemo(() => ({
        query: searchQuery || undefined,
        typ: filterTyp || undefined,
        page,
        size: pageSize,
        sortBy: 'datumCasOd',
        sortOrder: 'asc',
    }), [searchQuery, filterTyp, page, pageSize]);

    const { data, isLoading, error } = useSearchAkceQuery(searchParams);
    const deleteMutation = useDeleteAkceMutation();
    const sendNotifikaceMutation = useSendAkceNotifikaceMutation();

    // Dialog state
    const [openDialog, setOpenDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selectedAkce, setSelectedAkce] = useState<Akce | null>(null);

    // Card menu state
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [menuAkce, setMenuAkce] = useState<Akce | null>(null);

    const handleOpenCreate = () => {
        setSelectedAkce(null);
        setOpenDialog(true);
    };

    const handleOpenEdit = (akce: Akce) => {
        setSelectedAkce(akce);
        setOpenDialog(true);
    };

    const handleOpenDelete = (akce: Akce) => {
        setSelectedAkce(akce);
        setOpenDeleteDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedAkce(null);
    };

    const handleDelete = () => {
        if (selectedAkce) {
            deleteMutation.mutate(selectedAkce.id, {
                onSuccess: () => {
                    setOpenDeleteDialog(false);
                    setSelectedAkce(null);
                },
            });
        }
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, akce: Akce) => {
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
        setMenuAkce(akce);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setMenuAkce(null);
    };

    const handleSendNotifikace = (akce: Akce) => {
        sendNotifikaceMutation.mutate(akce.id);
        handleMenuClose();
    };

    const formatDateRange = (od: string, doo?: string): string => {
        const from = dayjs(od).format('D.M.YYYY HH:mm');
        if (!doo) return from;
        const to = dayjs(doo);
        if (dayjs(od).isSame(to, 'day')) {
            return `${from} – ${to.format('HH:mm')}`;
        }
        return `${from} – ${to.format('D.M.YYYY HH:mm')}`;
    };

    const akceList = data?.content || [];

    if (error) {
        return <Alert severity="error">Chyba při načítání akcí: {error.message}</Alert>;
    }

    return (
        <Box>
            <PageHeader
                title="Plánované akce"
                action={
                    isAdmin ? (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleOpenCreate}
                        >
                            Nová akce
                        </Button>
                    ) : undefined
                }
            />

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <TextField
                        label="Hledat"
                        value={searchQuery}
                        onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                        size="small"
                        sx={{ minWidth: 200 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Typ akce</InputLabel>
                        <Select
                            value={filterTyp}
                            label="Typ akce"
                            onChange={(e) => { setFilterTyp(e.target.value); setPage(0); }}
                        >
                            <MenuItem value="">Všechny</MenuItem>
                            {Object.entries(TYP_LABELS).map(([key, label]) => (
                                <MenuItem key={key} value={key}>{label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Paper>

            {/* Card list */}
            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : akceList.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">Žádné plánované akce</Typography>
                </Paper>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {akceList.map((akce) => (
                        <Paper
                            key={akce.id}
                            sx={{
                                p: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                cursor: 'pointer',
                                transition: 'box-shadow 0.2s, background-color 0.2s',
                                '&:hover': {
                                    boxShadow: 3,
                                    backgroundColor: 'action.hover',
                                },
                            }}
                            onClick={() => navigate(`/admin/akce/${akce.id}`)}
                        >
                            {/* Left: Type icon + chip */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, minWidth: 80 }}>
                                <TypIcon typ={akce.typ} />
                                <Chip
                                    label={TYP_LABELS[akce.typ] || akce.typ}
                                    color={TYP_COLORS[akce.typ] || 'default'}
                                    size="small"
                                    sx={{ fontSize: '0.7rem' }}
                                />
                            </Box>

                            {/* Center: Info */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="subtitle1" fontWeight="bold" noWrap>
                                    {akce.nazev}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, color: 'text.secondary' }}>
                                    <CalendarMonthIcon sx={{ fontSize: 16 }} />
                                    <Typography variant="body2">
                                        {formatDateRange(akce.datumCasOd, akce.datumCasDo)}
                                    </Typography>
                                </Box>
                                {akce.misto && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25, color: 'text.secondary' }}>
                                        <LocationOnIcon sx={{ fontSize: 16 }} />
                                        <Typography variant="body2" noWrap>
                                            {akce.misto}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>

                            {/* Right: ⋮ menu */}
                            <IconButton
                                size="small"
                                onClick={(e) => handleMenuOpen(e, akce)}
                            >
                                <MoreVertIcon />
                            </IconButton>
                        </Paper>
                    ))}
                </Box>
            )}

            {/* Pagination */}
            {(data?.totalElements ?? 0) > 0 && (
                <TablePagination
                    component="div"
                    count={data?.totalElements || 0}
                    page={page}
                    onPageChange={(_e, newPage) => setPage(newPage)}
                    rowsPerPage={pageSize}
                    onRowsPerPageChange={(e) => {
                        setPageSize(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[10, 20, 50]}
                    labelRowsPerPage="Řádků na stránce:"
                    labelDisplayedRows={({ from, to, count }) =>
                        `${from}–${to} z ${count !== -1 ? count : `více než ${to}`}`
                    }
                    getItemAriaLabel={(type) => type === 'next' ? 'Další stránka' : 'Předchozí stránka'}
                />
            )}

            {/* Card context menu */}
            <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
                onClick={(e) => e.stopPropagation()}
            >
                <MenuItem onClick={() => { if (menuAkce) navigate(`/admin/akce/${menuAkce.id}`); handleMenuClose(); }}>
                    <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Zobrazit detail</ListItemText>
                </MenuItem>
                {isAdmin && (
                    <MenuItem onClick={() => { if (menuAkce) handleOpenEdit(menuAkce); handleMenuClose(); }}>
                        <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>Upravit</ListItemText>
                    </MenuItem>
                )}
                {isAdmin && (
                    <MenuItem
                        onClick={() => { if (menuAkce) handleSendNotifikace(menuAkce); }}
                        disabled={sendNotifikaceMutation.isPending}
                    >
                        <ListItemIcon><NotificationsIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>Odeslat notifikaci</ListItemText>
                    </MenuItem>
                )}
                {isAdmin && [
                    <Divider key="divider" />,
                    <MenuItem
                        key="delete"
                        onClick={() => { if (menuAkce) handleOpenDelete(menuAkce); handleMenuClose(); }}
                        sx={{ color: 'error.main' }}
                    >
                        <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
                        <ListItemText>Smazat</ListItemText>
                    </MenuItem>,
                ]}
            </Menu>

            {/* Create/Edit Dialog (sdileny s detailem akce) */}
            <AkceFormDialog
                open={openDialog}
                akce={selectedAkce}
                onClose={handleCloseDialog}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
                <DialogTitle>Smazat akci?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Opravdu chcete smazat akci{' '}
                        <strong>{selectedAkce?.nazev}</strong>?
                        Budou smazány i všechny odpovědi.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteDialog(false)}>Zrušit</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDelete}
                        disabled={deleteMutation.isPending}
                    >
                        {deleteMutation.isPending && (
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                        )}
                        Smazat
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AkcePage;
